import { useCallback, useMemo, useRef, useState } from 'react';
import { ApolloError, DocumentNode, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  KommoLeadBoardColumnDocument,
  KommoLeadBoardColumnQuery,
  KommoLeadStageTotalsDocument,
  KommoLossReasonListQuery,
  KommoResponsibleListQuery,
  useAssignKommoLeadResponsibleMutation,
  useKommoLeadAssignmentQuery,
  useKommoLeadBoardColumnQuery,
  useKommoLeadsCreatedRecentlyQuery,
  useKommoLeadStageTotalsQuery,
  useKommoLossReasonListQuery,
  useKommoResponsibleListQuery,
  useMoveKommoLeadStageMutation,
} from 'core/graphql';
import { ToastTypes } from 'types';

import {
  ILeadBoardStage,
  ILeadStageCount,
  LEAD_BOARD_PAGE,
  toLeadBoardColumnVariables,
  toLeadStageCountVariables,
  toRecentLeadsVariables,
  toUnassignedCountVariables,
} from './leadsBoardModel';
import { ILeadsFilters } from './leadsModel';
import { useLeadErrorText, useLeadsNotify } from './leadsFeedback';
import { useLeadPermissions } from './useLeadPermissions';

/** ТЗ 0106 — доска «Воронка», перенос этапа и смена ответственного. */

export type IKommoLeadBoardRow = KommoLeadBoardColumnQuery['boardColumn']['items'][number];
export type IKommoLossReason = KommoLossReasonListQuery['kommoLossReasons']['items'][number];
export type IKommoResponsibleOption = KommoResponsibleListQuery['kommoLeadResponsibles'][number];

// Пустые значения — константы модуля: свежий `[]` в зависимостях эффекта
// вызывающего крутил бы эффект без конца.
const NO_ROWS: IKommoLeadBoardRow[] = [];
const NO_COUNTS: ILeadStageCount[] = [];
const NO_REASONS: IKommoLossReason[] = [];
const NO_PEOPLE: IKommoResponsibleOption[] = [];

/**
 * Перечитать запрос из сети и дождаться ответа; ошибка глотается (экран
 * покажет её сам). Отдельным `client.query`, а не `refetch()` наблюдателя:
 * перечитывание наблюдателя Apollo 3.3 может слиться с уже идущим (или
 * отменённым) запросом колонки и не дойти до сети — доска ждала бы зря.
 * Ответ ложится в кэш, и колонка (её наблюдатель) получает его сама.
 */
const useNetworkRefresh = (
  query: DocumentNode,
  variables: Record<string, unknown>,
  enabled: boolean,
) => {
  const client = useApolloClient();
  const key = JSON.stringify(variables);
  return useCallback((): Promise<void> => {
    if (!enabled) return Promise.resolve();
    return client
      .query({
        query,
        variables: JSON.parse(key),
        fetchPolicy: 'network-only',
        context: { queryDeduplication: false },
      })
      .then(() => undefined)
      .catch(() => undefined);
  }, [client, query, key, enabled]);
};

/**
 * Колонка доски: лиды ОДНОГО этапа под фильтрами таблицы. Пока едет ответ
 * на новые фильтры, колонка держит прошлую выдачу (`previousData`) — не
 * мигает пустотой. Упал ТЕКУЩИЙ запрос — прошлая выдача (другие фильтры) не
 * выдаётся за актуальную: колонка пуста и с ошибкой «Повторить».
 *
 * ТЗ 0128: первая страница — `LEAD_BOARD_PAGE`; «ещё» (`loadMore`) —
 * `fetchMore` следующей страницы с `offset` = сколько загружено: политика
 * кэша `boardColumn` дописывает её к колонке (без потолка и без
 * перечитывания с нуля). `refresh` (после переноса) перечитывает из сети
 * ВСЁ загруженное одним запросом — колонка не схлопывается до 30.
 */
export const useKommoLeadBoardColumn = (
  filters: ILeadsFilters,
  stage: Pick<ILeadBoardStage, 'id' | 'isClosed'>,
  enabled = true,
) => {
  const { canUse } = useLeadPermissions();
  const { id: stageId, isClosed: stageClosed } = stage;
  const variables = useMemo(
    () =>
      toLeadBoardColumnVariables(
        filters,
        { id: stageId, isClosed: stageClosed },
        LEAD_BOARD_PAGE,
      ),
    [filters, stageId, stageClosed],
  );
  const live = canUse && enabled;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
    fetchMore,
  } = useKommoLeadBoardColumnQuery({
    variables,
    skip: !live,
    fetchPolicy: 'cache-and-network',
    // После первого ответа — из кэша: иначе Apollo 3.3 перечитывает
    // `cache-and-network` из сети на каждую запись в кэш (перенос, соседняя
    // колонка) — двойные запросы итогов. Смена фильтров снова идёт в сеть.
    nextFetchPolicy: 'cache-first',
  });
  const page = (data ?? (error ? undefined : previousData))?.boardColumn;
  const rows = page?.items ?? NO_ROWS;
  const total = page?.total ?? null;
  const loadedRef = useRef(0);
  loadedRef.current = data?.boardColumn.items.length ?? 0;

  const [moreBusy, setMoreBusy] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const moreRef = useRef(false);
  const loadMore = useCallback(() => {
    if (moreRef.current || !loadedRef.current) return;
    moreRef.current = true;
    setMoreBusy(true);
    setMoreFailed(false);
    fetchMore({
      variables: { offset: loadedRef.current, limit: LEAD_BOARD_PAGE },
    })
      .catch(() => setMoreFailed(true))
      .finally(() => {
        moreRef.current = false;
        setMoreBusy(false);
      });
  }, [fetchMore]);

  const client = useApolloClient();
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  /** Перечитать из сети всё загруженное и дождаться ответа (доска после переноса). */
  const refresh = useCallback((): Promise<void> => {
    if (!live) return Promise.resolve();
    return client
      .query({
        query: KommoLeadBoardColumnDocument,
        variables: {
          ...variables,
          offset: 0,
          limit: Math.max(LEAD_BOARD_PAGE, loadedRef.current),
        },
        fetchPolicy: 'network-only',
        context: { queryDeduplication: false },
      })
      .then(() => undefined)
      .catch(() => undefined);
  }, [client, live, variables]);

  return {
    rows,
    total,
    loading: loading && !page,
    failed: Boolean(error),
    /** Есть ещё страницы (сервер знает больше, чем загружено). */
    hasMore: total !== null && rows.length < total && !error,
    loadMore,
    moreBusy,
    moreFailed,
    reload,
    refresh,
  };
};

/**
 * Число и сумма лидов на каждом этапе под фильтрами (кроме этапа).
 * `ready` — счётчики ТЕКУЩИХ фильтров (или прошлых, пока едут новые); упал
 * текущий запрос — счётчиков нет и `failed`.
 */
export const useKommoLeadStageTotals = (
  filters: ILeadsFilters,
  enabled = true,
) => {
  const { canUse } = useLeadPermissions();
  const variables = useMemo(() => toLeadStageCountVariables(filters), [
    filters,
  ]);
  const { data, previousData, error, refetch } = useKommoLeadStageTotalsQuery({
    variables,
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-and-network',
    // После первого ответа — из кэша: иначе Apollo 3.3 перечитывает
    // `cache-and-network` из сети на каждую запись в кэш (перенос, соседняя
    // колонка) — двойные запросы итогов. Смена фильтров снова идёт в сеть.
    nextFetchPolicy: 'cache-first',
  });
  const shown = data ?? (error ? undefined : previousData);
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  const refresh = useNetworkRefresh(
    KommoLeadStageTotalsDocument,
    variables,
    canUse && enabled,
  );
  return {
    counts: shown?.kommoLeadStageCounts ?? NO_COUNTS,
    ready: Boolean(shown),
    failed: Boolean(error),
    reload,
    refresh,
  };
};

/** Те же счётчики только по лидам без ответственного — плитка-итог. */
export const useKommoLeadUnassignedTotals = (
  filters: ILeadsFilters,
  enabled = true,
) => {
  const { canUse } = useLeadPermissions();
  const variables = useMemo(() => toUnassignedCountVariables(filters), [
    filters,
  ]);
  const { data, previousData, refetch } = useKommoLeadStageTotalsQuery({
    variables,
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-and-network',
    // После первого ответа — из кэша: иначе Apollo 3.3 перечитывает
    // `cache-and-network` из сети на каждую запись в кэш (перенос, соседняя
    // колонка) — двойные запросы итогов. Смена фильтров снова идёт в сеть.
    nextFetchPolicy: 'cache-first',
  });
  const shown = data ?? previousData;
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  const refresh = useNetworkRefresh(
    KommoLeadStageTotalsDocument,
    variables,
    canUse && enabled,
  );
  return {
    counts: shown?.kommoLeadStageCounts ?? NO_COUNTS,
    ready: Boolean(shown),
    reload,
    refresh,
  };
};

/**
 * Плитка «Новые за 7 дней»: сколько лидов создано в Kommo за последние 7
 * дней по Варшаве под фильтрами списка; `null` — ответа ещё нет.
 */
export const useKommoLeadsCreatedRecently = (
  filters: ILeadsFilters,
  enabled = true,
) => {
  const { canUse } = useLeadPermissions();
  const { data, previousData } = useKommoLeadsCreatedRecentlyQuery({
    variables: toRecentLeadsVariables(filters),
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-and-network',
    // ТЗ 0128: после первого ответа — из кэша (см. колонку).
    nextFetchPolicy: 'cache-first',
  });
  const shown = data ?? previousData;
  return { total: shown ? shown.kommoLeads.total : null };
};

/** Причины отказа Kommo — только когда открыт диалог закрытия в 143. */
export const useKommoLossReasons = (enabled: boolean) => {
  const { canUse } = useLeadPermissions();
  const { data, loading, error } = useKommoLossReasonListQuery({
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-first',
  });
  const payload = data?.kommoLossReasons;
  return {
    reasons: payload?.items ?? NO_REASONS,
    /** Kommo не ответил (`error`) или запрос упал. */
    errorCode: payload?.error?.code ?? (error ? 'NETWORK' : null),
    loading: loading && !payload,
  };
};

/** Кого можно назначить ответственным — выпадашка в карточке лида. */
export const useKommoResponsibles = (enabled = true) => {
  const { canUse } = useLeadPermissions();
  const { data, loading, error } = useKommoResponsibleListQuery({
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-first',
  });
  return {
    people: data?.kommoLeadResponsibles ?? NO_PEOPLE,
    loading: loading && !data,
    failed: Boolean(error) && !data,
  };
};

/** Строка ответственного Kommo и «на этапе с» — выбор в карточке лида. */
export const useKommoLeadAssignment = (leadId: string | null) => {
  const { canUse } = useLeadPermissions();
  const { data } = useKommoLeadAssignmentQuery({
    variables: { id: leadId || '' },
    skip: !canUse || !leadId,
    fetchPolicy: 'cache-and-network',
  });
  return { assignment: data?.kommoLead ?? null };
};

export interface ILeadWriteResult {
  ok: boolean;
  /** Код отказа (`customErrors.code`, `NETWORK`, `NO_ACCESS`) или `null`. */
  code: string | null;
}

const failure = (caught: unknown): ILeadWriteResult => ({
  ok: false,
  code:
    caught instanceof ApolloError && caught.graphQLErrors.length > 0
      ? 'NO_ACCESS'
      : 'NETWORK',
});

/**
 * Запись в Kommo (перенос этапа или смена ответственного): отказ — тост с
 * переводом кода; удача — ответ уже лёг в кэш `KommoLeadType:<id>`. Двойной
 * клик гасится: второй вызов, пока первый в пути, возвращает отказ без
 * запроса.
 */
const useLeadWrite = () => {
  const { t } = useTranslation();
  const notify = useLeadsNotify();
  const errorText = useLeadErrorText();
  const inFlight = useRef(new Set<string>());

  return useCallback(
    async (
      key: string,
      failTitle: string,
      run: () => Promise<{
        success?: boolean | null;
        customErrors?: ReadonlyArray<{
          code?: string | null;
          message?: string | null;
        } | null> | null;
      } | null>,
    ): Promise<ILeadWriteResult> => {
      if (inFlight.current.has(key)) return { ok: false, code: 'BUSY' };
      inFlight.current.add(key);
      try {
        const payload = await run();
        const errors = (
          payload?.customErrors ?? []
        ).filter((error): error is NonNullable<typeof error> => Boolean(error));
        if (!payload?.success || errors.length) {
          notify(
            ToastTypes.error,
            t(failTitle),
            errors.map((error) => errorText(error)).join('\n') ||
              errorText({ code: 'ERROR', message: '' }),
          );
          return { ok: false, code: errors[0]?.code ?? 'ERROR' };
        }
        return { ok: true, code: null };
      } catch (caught) {
        const result = failure(caught);
        notify(
          ToastTypes.error,
          t(failTitle),
          errorText({ code: result.code, message: '' }),
        );
        return result;
      } finally {
        inFlight.current.delete(key);
      }
    },
    [errorText, notify, t],
  );
};

/**
 * Что перечитать после переноса этапа (по имени — только активные запросы).
 * Карточка лида перечитывает и доску (колонки и итоги): иначе перенос из
 * карточки не виден на доске под ней. Доска сама перечитывает свои колонки
 * и итоги и ждёт их (`selfRefresh`) — второй раз по имени их не трогаем.
 */
export const leadMoveRefetchQueries = (selfRefresh = false): string[] => [
  'KommoLeadStageList',
  'KommoLeadsCreatedRecently',
  'LeadJourneyTimeline',
  ...(selfRefresh ? [] : ['KommoLeadStageTotals', 'KommoLeadBoardColumn']),
];

/**
 * После смены ответственного: колонки доски и итоги (фильтр «Мои» и плитка
 * «без ответственного» зависят от него).
 */
export const LEAD_ASSIGN_REFETCH_QUERIES = [
  'KommoLeadBoardColumn',
  'KommoLeadStageTotals',
];

interface IMoveKommoLeadOptions {
  /** Доска перечитывает колонки и итоги сама (и ждёт ответа). */
  selfRefresh?: boolean;
}

/** Перенести лида на этап; в 143 — с причиной отказа Kommo. */
export const useMoveKommoLead = ({
  selfRefresh = false,
}: IMoveKommoLeadOptions = {}) => {
  const [moveMut, { loading }] = useMoveKommoLeadStageMutation();
  const write = useLeadWrite();
  const move = useCallback(
    (id: string, statusId: string, lossReasonId?: string | null) =>
      write(`move-${id}`, 'leads.board.toast.moveFailed', async () => {
        const { data } = await moveMut({
          variables: { id, statusId, lossReasonId: lossReasonId || null },
          refetchQueries: leadMoveRefetchQueries(selfRefresh),
        });
        return data?.moveKommoLead ?? null;
      }),
    [moveMut, write, selfRefresh],
  );
  return { move, moving: loading };
};

/** Сменить ответственного (`userId` — строка из `kommoLeadResponsibles`). */
export const useAssignKommoLeadResponsible = () => {
  const [assignMut, { loading }] = useAssignKommoLeadResponsibleMutation();
  const write = useLeadWrite();
  const assign = useCallback(
    (id: string, userId: string) =>
      write(`assign-${id}`, 'leads.board.toast.assignFailed', async () => {
        const { data } = await assignMut({
          variables: { id, userId },
          refetchQueries: LEAD_ASSIGN_REFETCH_QUERIES,
        });
        return data?.setKommoLeadResponsible ?? null;
      }),
    [assignMut, write],
  );
  return { assign, assigning: loading };
};
